// @flow
import queryString from 'query-string';
import * as React from 'react';
import { hydrate } from 'react-dom';
import { renderToString, renderToStaticMarkup } from 'react-dom/server';
import { Router, StaticRouter, useLocation } from 'react-router-dom';
import { initXMLHttpRequestActiveCounter } from 'utils/http';
import { Provider } from 'react-redux';
import hypernova, { serialize, load } from 'hypernova';
import { ServerStyleSheet } from 'styled-components';
import { createBrowserHistory as createHistory } from 'history';
import Loadable from 'react-loadable';
import configureTrackJs from 'utils/configureTrackJs';
import {
  bootstrapAnalytics,
  trackEvent,
  registerAbExperiments
} from 'utils/analytics';
import { fetchCurrentUser } from 'api/currentUser';
import renderAmpPage from 'utils/renderAmpPage';
import WithConfig from 'containers/WithConfig';
import {
  updateCurrentUser,
  setABExperimentBucket
} from '../redux/main/actions/currentUser';

const { useEffect } = React;

const performanceMark = (...args) => {
  if (
    window &&
    window.performance &&
    typeof window.performance.mark === 'function'
  ) {
    window.performance.mark(...args);
  }
};

const AppContainer = ({ children }: { children: React.Node }) => {
  const location = useLocation();

  useEffect(
    () => {
      window.scrollTo(0, 0);
    },
    [location.pathname]
  );
  return children;
};

// Accept a query param to override an AB experiment bucket:
// ?ab_test[button_color]=red
const overrideABExperiments = (store, currentUser) => {
  const queryParams = queryString.parse(window.location.search);
  const overrides = Object.keys(queryParams).reduce((acc, param) => {
    const matches = param.match(/^ab_test\[([^\]]+)\]$/);
    if (matches) {
      acc.push({ id: matches[1], bucket: queryParams[param] });
    }
    return acc;
  }, []);

  overrides.forEach(({ id, bucket }) => {
    const experiment = currentUser.abExperiments.find(e => e.id === id);
    if (!experiment || experiment.bucket !== bucket) {
      store.dispatch(setABExperimentBucket(id, bucket));
    }
  });
};

const renderReact = (
  name: string,
  Component: React.ComponentType<any>,
  createStore: Function
) =>
  hypernova({
    server() {
      return Loadable.preloadAll().then(() => props => {
        const componentWrapper = ({
          url,
          state = {},
          ampEnabled,
          config
        }: {
          url: string,
          state: Object,
          ampEnabled: boolean,
          config: Object
        }) => {
          const store = createStore(state);
          return (
            <Provider store={store}>
              <WithConfig config={config}>
                <StaticRouter location={url} context={{}}>
                  <Component ampEnabled={ampEnabled} />
                </StaticRouter>
              </WithConfig>
            </Provider>
          );
        };

        const sheet = new ServerStyleSheet();
        const element = React.createElement(componentWrapper, props);

        if (props.ampEnabled) {
          const {
            title,
            description,
            canonicalUrl,
            altUrls,
            ampTrackingId
          } = props;
          const body = renderToStaticMarkup(
            sheet.collectStyles(element)
          ).replace(/data-style/g, 'style');
          const styleTags = sheet
            .getStyleTags()
            .replace(/<\/?style[^>]*>/g, '')
            .replace(/@media screen and \(min-width:(576|768|992)px\){}/g, '');
          return renderAmpPage({
            title,
            description,
            styles: styleTags,
            body,
            canonicalUrl,
            altUrls,
            ampTrackingId
          });
        }

        const html = renderToString(sheet.collectStyles(element));
        const styleTags = sheet
          .getStyleTags()
          .replace(/@media screen and \(min-width:(576|768|992)px\){}/g, '');
        const markup = serialize(name, html, {
          config: props.config,
          state: props.state
        });
        return `${styleTags}\n${markup}`;
      });
    },

    client() {
      performanceMark('Starting app');
      const browserHistory = createHistory();
      // Track js "pageViews" using GTM
      browserHistory.listen((location, action) => {
        if (action !== 'REPLACE') {
          trackEvent('JS Page View');
        }
      });

      if (window.Idealist && window.Idealist.debugXhr) {
        initXMLHttpRequestActiveCounter();
      }

      const payloads = load(name);
      if (payloads.length > 0) {
        const payload = payloads[0];
        const {
          node,
          data: { state, config }
        } = payload;
        if (node) {
          Loadable.preloadReady().then(() => {
            const store = createStore(state);
            let currentUser = store.getState().currentUser;
            if (currentUser && currentUser.isLoaded) {
              configureTrackJs(currentUser.id);
              bootstrapAnalytics({
                user: currentUser
              });
              store.subscribe(() => {
                registerAbExperiments(
                  store.getState().currentUser.abExperiments
                );
              });
              overrideABExperiments(store, currentUser);
              performanceMark('User data first load');
            } else {
              fetchCurrentUser().then(fetchedUser => {
                // FIXME: refactor redux store to get rid of this workaround
                // This fixes a race condition where the user info is being fetched while the
                // search location is being set
                currentUser = store.getState().currentUser;
                if (!currentUser.isLoaded && currentUser.searchLocation) {
                  fetchedUser.searchLocation = currentUser.searchLocation;
                }
                store.dispatch(updateCurrentUser(fetchedUser));
                configureTrackJs(fetchedUser.id);
                bootstrapAnalytics({
                  user: fetchedUser
                });
                store.subscribe(() => {
                  registerAbExperiments(
                    store.getState().currentUser.abExperiments
                  );
                });
                overrideABExperiments(store, fetchedUser);
                performanceMark('User data first load');
              });
            }

            hydrate(
              <Provider store={store}>
                <WithConfig config={config}>
                  <Router history={browserHistory}>
                    <AppContainer>
                      <Component />
                    </AppContainer>
                  </Router>
                </WithConfig>
              </Provider>,
              node,
              () => performanceMark('App ready')
            );
          });
        }
      }
    }
  });

export default renderReact;
